import { useCallback, useEffect, useRef } from "react";

const DOMO_ORIGIN_RE = /^https:\/\/([a-z0-9-]+\.)?domo\.com$/i;
const isDomoOrigin = (origin) => DOMO_ORIGIN_RE.test(origin);

const getStorageKey = (content) => {
  const id = content?.page_id;
  return `domo:filters:${id}`;
};

const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const normalizeFilter = (f) => {
  if (!f) return null;
  const column = typeof f.column === "string" ? f.column : f.columnId || f.column?.id || f.column?.name;

  if (!column || !f.operand) return null;

  const out = {
    column,
    operand: f.operand,
    values: Array.isArray(f.values) ? f.values : [],
    dataSourceId: f.dataSourceId,
  };
  if (f.dataType) out.dataType = f.dataType;
  return out;
};

const normalizeFilters = (filters) => (Array.isArray(filters) ? filters : []).map(normalizeFilter).filter(Boolean);

const readFilters = (content) => {
  try {
    const raw = localStorage.getItem(getStorageKey(content));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.filters || Date.now() - parsed.savedAt > TTL_MS) return null;

    const filters = normalizeFilters(parsed?.filters);
    return filters;
  } catch {
    return null;
  }
};

const writeFilters = (content, filters) => {
  try {
    localStorage.setItem(getStorageKey(content), JSON.stringify({ filters, savedAt: Date.now() }));
  } catch (err) {
    console.warn("Domo: failed to persist filters", err);
  }
};

const removeFilters = (content) => {
  try {
    localStorage.removeItem(getStorageKey(content));
    console.log("Filters session removed successfully.");
  } catch (err) {
    console.warn("Domo: failed to remove filters", err);
  }
};

const buildPayload = (filters) => ({
  id: `setFilters_${Date.now()}`,
  jsonrpc: "2.0",
  method: "/v1/filters/apply",
  params: { filters: normalizeFilters(filters) },
});

export default function useDomoFilterPersistence(content) {
  const portRef = useRef(null);
  const pendingRef = useRef(null);
  const appReadyRef = useRef(false);

  const send = useCallback((filters) => {
    if (!Array.isArray(filters) || filters.length === 0) {
      console.warn("Filters are empty, not sending them.");
      return false;
    }

    const port = portRef.current;
    if (!port) return false;
    const payload = buildPayload(filters);
    port.postMessage(payload);
    return true;
  }, []);

  useEffect(() => {
    if (!content) return;

    const handleHandshake = (event) => {
      if (!isDomoOrigin(event.origin)) return;
      if (!event.ports || !event.ports[0]) return;

      const port = event.ports[0];
      port.start();
      portRef.current = port;

      port.onmessage = (portEvent) => {
        const data = portEvent.data;
        if (!data || typeof data !== "object") return;

        if (!appReadyRef.current) {
          appReadyRef.current = true;

          if (pendingRef.current) {
            send(pendingRef.current);
            pendingRef.current = null;
          }
        }

        if (data.method === "/v1/onFiltersChange") {
          const filters = normalizeFilters(data.params?.filters ?? []);

          if (filters.length === 0) {
            console.warn("[Domo] Received empty filter array, skipping.");
            return;
          }

          console.log("[Domo] Normalized filters:", filters);
          writeFilters(content, filters);
        }
      };

      if (pendingRef.current) {
        send(pendingRef.current);
        pendingRef.current = null;
      }
    };

    window.addEventListener("message", handleHandshake);

    return () => {
      window.removeEventListener("message", handleHandshake);
      if (portRef.current) {
        try {
          portRef.current.close?.();
        } catch { }
      }
      portRef.current = null;
      pendingRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const applySavedFilters = useCallback(() => {
    const saved = content?.updatedUserFilters || [];
    if (!saved?.length) return;

    if (!portRef.current || !appReadyRef.current) {
      pendingRef.current = saved;
    } else {
      send(saved);
    }
  }, [content, send]);

  const removeSavedFilters = useCallback(() => {
    removeFilters(content);
  }, [content]);

  return { applySavedFilters, removeSavedFilters, readFilters, getStorageKey };
}
